import { useState } from 'react';

import { api } from '@/services';
import { FormFeedback } from '@/components';
import { useAuth, useFeedback } from '@/hooks';
import { validators, getErrorDetails } from '@/utils';

import InputGroup from './InputGroup';
import ResendButton from './ResendButton';

const steps = {
  otp: 'otp',
  email: 'email',
};

function PrimaryAuthForm({ botDetected }) {
  const [otp, setOTP] = useState('');
  const [email, setEmail] = useState('');
  const [step, setStep] = useState(steps.email);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { onAuthorized } = useAuth();
  const { error, message, setError, setMessage, hasFeedback } = useFeedback();

  const handleEmailChange = (value) => {
    setEmail(value);
    setError(null);
  };

  const handleLogin = async (email) => {
    if (botDetected) return;

    setOTP('');
    setError(null);

    const { valid, message } = validators.validateEmail(email);

    if (!valid) return setError(message);

    try {
      setIsSubmitting(true);
      await api.login(email);
      setMessage('A code has been sent to your email.');
      setStep(steps.otp);
    } catch (error) {
      const { is429, message } = getErrorDetails(error);

      if (is429) {
        setError(message);
      } else {
        setError('Failed to send code. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOTPChange = (value) => {
    setOTP(value);
    setError(null);
  };

  const handleOTPVerify = async (otp) => {
    if (botDetected) return;

    setError(null);

    const { valid, message } = validators.validateOTP(otp);

    if (!valid) return setError(message);

    try {
      setIsSubmitting(true);
      await api.verifyOTP(email, otp);
      onAuthorized();
    } catch (error) {
      const { is401, is429, message } = getErrorDetails(error);

      if (is401) {
        setError(
          <>
            {message} <ResendButton onClick={handleLogin} />
          </>
        );
      } else if (is429) {
        setError(message);
      } else {
        setError(
          <>
            Code verification failed. <ResendButton onClick={handleLogin} />
          </>
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-3">
      {step === steps.email && (
        <InputGroup
          type="email"
          value={email}
          error={error}
          caption="Send Code"
          onSubmit={handleLogin}
          isSubmitting={isSubmitting}
          onChange={handleEmailChange}
          placeholder="Enter your email"
          submittingCaption="Sending Code"
        />
      )}

      {step === steps.otp && (
        <InputGroup
          allowPaste
          value={otp}
          error={error}
          caption="Verify Code"
          onChange={handleOTPChange}
          onSubmit={handleOTPVerify}
          isSubmitting={isSubmitting}
          placeholder="Enter your code"
          submittingCaption="Verifying Code"
        />
      )}

      <div className="mt-1">
        {hasFeedback ? (
          <FormFeedback type={error ? 'danger' : 'success'}>
            {error || message}
          </FormFeedback>
        ) : (
          <FormFeedback type="muted">
            {step === steps.email ? (
              <>We’ll email you a one-time sign in code.</>
            ) : (
              <>Check your email for the code.</>
            )}
          </FormFeedback>
        )}
      </div>
    </div>
  );
}

export default PrimaryAuthForm;
